<template>
  <div class="work-order-task-data">
    <div class="work-order-task-data__main">
      <div class="work-order-task-data__fields">
        <div
          v-for="(row, rowIndex) in fieldRows"
          :key="row.title || rowIndex"
          class="work-order-task-data__group"
        >
          <div
            v-if="row.title"
            class="work-order-task-data__group-title"
          >
            {{ row.title }}
          </div>

          <fields-renderer
            :config="{ columnsCount: row.columnsCount, fields: row.fields }"
            :form-data="formData"
            :fields-config="fieldsConfig"
            :validation-errors="validationErrors"
            :initial-autocompletes="initialAutocompletes"
            :permissions="permissions"
            :edit="false"
            :create-mode="false"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
          />
        </div>
      </div>

      <!--    A fixed height, whatever the form beside it comes to - see the styles
              below.-->
      <div class="work-order-task-data__side">
        <div class="work-order-task-data__side-inner">
          <work-orders-map-panel
            :streets="streets"
          />
        </div>
      </div>
    </div>

    <winter-service-log-history
      :record="formData"
      :translation-namespace="NS"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
import WorkOrdersMapPanel from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/components/WorkOrdersMapPanel.vue'
import WinterServiceLogHistory from '@/modules/road-maintenance-module/winter-service-v2/common/components/WinterServiceLogHistory.vue'
import { useTaskStreetPaths } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskStreetPaths'
import { WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its `rows` hold the grouped
  // field lists (see useWorkOrderTaskData for why they live there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  // Declared but never honoured: the whole screen is read-only, so the renderers below
  // are handed `false` whatever the tab layout passes in. They are taken as props
  // rather than left to fall through as attributes on the root element.
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const { loadStreets } = useTaskStreetPaths()

const streets = ref([])

const fieldRows = computed(() => props.config?.rows || [])

// The order endpoint reports no geometry, so the route drawn beside the form has to be
// fetched from the streets of the task the order runs.
const taskId = computed(() => props.formData?.task_data?.task?.id || null)

const loadTaskStreets = async () => {
  if (!taskId.value) {
    streets.value = []
    return
  }

  streets.value = await loadStreets([taskId.value])
}

onMounted(loadTaskStreets)

watch(taskId, loadTaskStreets)
</script>

<style scoped>
.work-order-task-data {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
}

.work-order-task-data__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  /* The map keeps its own height, so the row must not stretch its cell to the form's
     - see the side column below. */
  align-items: start;
}

.work-order-task-data__fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.work-order-task-data__group {
  padding: 12px 20px 4px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

.work-order-task-data__group-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

/* A fixed height rather than however tall the form beside it happens to be: the two
   tabs of this screen carry different field counts, and a map that follows them is a
   different map on each. */
.work-order-task-data__side {
  min-width: 0;
  height: 480px;
}

.work-order-task-data__side-inner {
  height: 100%;
}

/* Stacked under the form, and shorter: the map has the full width to itself here. */
@media (max-width: 960px) {
  .work-order-task-data__main {
    grid-template-columns: minmax(0, 1fr);
  }

  .work-order-task-data__side {
    height: 320px;
  }
}
</style>
